/**
 * CustomExerciseLibrary.jsx — "Ma bibliothèque d'exercices créés" : le client crée ses
 * propres exercices, génère GRATUITEMENT un texte d'exécution + conseils pro (IA), puis
 * génère un média IA avec des crédits (5 inclus/mois pour tout abonnement actif + packs
 * achetables via Stripe). Coûts : image statique -2, storyboard -1, vidéo Sora 2 -5.
 * Comme côté admin, AUCUN média ne peut être généré sans texte d'exécution existant
 * d'abord — il ancre le prompt pour un rendu fidèle au mouvement réellement décrit.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, Sparkles, Plus, X, Loader2, Coins, ImageIcon, Clapperboard,
  Trash2, RefreshCw, AlertTriangle, Wand2, HelpCircle, ChevronDown, Check, FileText, Lightbulb, Share2,
  UploadCloud, ImageOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { useClientAuth } from '../../../context/ClientAuthContext';
import { PushOptIn } from '../PushOptIn';

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

const MEDIA_LABELS = {
  image: { fr: 'Image', en: 'Image', icon: ImageIcon, cost: 2 },
  storyboard: { fr: 'Storyboard', en: 'Storyboard', icon: Sparkles, cost: 1 },
  video: { fr: 'Vidéo', en: 'Video', icon: Clapperboard, cost: 5 },
};

const ACCEPTED_UPLOAD_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/** Lit un fichier image local (validé JPG/PNG/WebP, 5 Mo max) et renvoie { base64, contentType,
 * previewUrl } — utilisé pour l'upload "telle quelle" ET la référence IA côté client. */
const readUploadFile = (file) => new Promise((resolve, reject) => {
  if (!ACCEPTED_UPLOAD_TYPES.includes(file.type)) { reject("Format non supporté (JPG, PNG ou WebP uniquement)."); return; }
  if (file.size > MAX_UPLOAD_BYTES) { reject('Image trop volumineuse (5 Mo maximum).'); return; }
  const reader = new FileReader();
  reader.onload = () => resolve({ base64: reader.result.split(',')[1], contentType: file.type, previewUrl: reader.result });
  reader.onerror = () => reject('Lecture du fichier échouée.');
  reader.readAsDataURL(file);
});

// Exemples réels tirés de la Bibliothèque V2 admin — pour montrer un aperçu fidèle du
// rendu de chaque type de média avant que le client ne dépense ses crédits.
const GUIDE_EXAMPLES = [
  { type: 'image', label: { fr: 'Image', en: 'Image' }, src: '/api/exercise-media/body-up/exercises-v2/f54eceab5aa8-developpe_couche_a_la_machine_convergente-732ccdee.png' },
  { type: 'storyboard', label: { fr: 'Storyboard', en: 'Storyboard' }, src: '/api/exercise-media/body-up/exercises-v2-storyboard/d2c1e2c59263-developpe_couche-00c56567.png' },
  { type: 'video', label: { fr: 'Vidéo Sora 2', en: 'Sora 2 video' }, src: '/api/exercise-media/body-up/exercises-v2-video/e4359b400fb9-squat_au_poids_du_corps-d1d6258e.mp4' },
];

const GuideModal = ({ en, open, onClose }) => {
  const L = (fr, eng) => (en ? eng : fr);
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-0 sm:p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="custom-ex-guide-modal"
        >
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="relative w-full sm:max-w-2xl bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-400" />
                <h3 className="text-lg font-bold text-white">{L('Guide de création d\'exercice', 'Exercise creation guide')}</h3>
              </div>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="custom-ex-guide-close"><X className="w-5 h-5" /></button>
            </div>

            <p className="text-xs font-bold tracking-wider text-primary mb-3">{L('COMMENT ÇA MARCHE — 3 ÉTAPES', 'HOW IT WORKS — 3 STEPS')}</p>
            <div className="space-y-3 mb-6">
              {[
                { n: '1', t: L('Créez votre exercice', 'Create your exercise'), d: L('Donnez-lui un nom précis, son groupe musculaire, le matériel utilisé et, si possible, une courte description du mouvement — plus c\'est précis, meilleur sera le résultat.', 'Give it a precise name, its muscle group, the equipment used and, if possible, a short movement description — the more precise, the better the result.') },
                { n: '2', t: L('Générez le texte d\'exécution (gratuit)', 'Generate the execution text (free)'), d: L('Notre IA rédige l\'explication technique + 3 conseils pro, exactement comme pour tous les exercices de la bibliothèque. C\'est ce texte qui garantit un média fidèle au bon mouvement — impossible de générer une image ou une vidéo sans lui.', 'Our AI writes the technical explanation + 3 pro tips, exactly like every exercise in the library. This text is what guarantees a media faithful to the correct movement — impossible to generate an image or video without it.') },
                { n: '3', t: L('Générez le média avec vos crédits', 'Generate the media with your credits'), d: L('Une fois le texte validé, choisissez Image, Storyboard ou Vidéo Sora 2 — le même moteur IA que la bibliothèque officielle Body-Up.', 'Once the text is validated, choose Image, Storyboard or Sora 2 Video — the same AI engine as the official Body-Up library.') },
              ].map((s) => (
                <div key={s.n} className="flex gap-3 rounded-2xl bg-white/[0.04] border border-white/10 p-4">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center text-white font-black text-sm shrink-0">{s.n}</div>
                  <div>
                    <p className="font-bold text-white text-sm">{s.t}</p>
                    <p className="text-xs text-zinc-400 leading-relaxed mt-0.5">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-xs font-bold tracking-wider text-amber-400 mb-3">{L('APERÇU DU RENDU AVANT D\'ACHETER', 'PREVIEW BEFORE YOU BUY')}</p>
            <div className="grid grid-cols-3 gap-2.5 mb-6" data-testid="custom-ex-guide-examples">
              {GUIDE_EXAMPLES.map((ex) => (
                <div key={ex.type} className="rounded-xl overflow-hidden border border-white/10 bg-black">
                  <div className="aspect-square bg-zinc-950">
                    {ex.type === 'video' ? (
                      <video src={`${backendUrl}${ex.src}`} className="w-full h-full object-cover" autoPlay loop muted playsInline />
                    ) : (
                      <img src={`${backendUrl}${ex.src}`} alt={ex.label.fr} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="px-2 py-1.5 flex items-center justify-between bg-white/[0.03]">
                    <span className="text-[10px] font-bold text-zinc-300">{en ? ex.label.en : ex.label.fr}</span>
                    <span className="text-[10px] font-black text-amber-400">-{MEDIA_LABELS[ex.type].cost}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl bg-gradient-to-br from-amber-500/10 to-zinc-900 border border-amber-500/20 p-4 flex items-start gap-2.5">
              <Coins className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-xs text-zinc-300 leading-relaxed">
                {L(
                  '5 crédits offerts chaque mois avec votre abonnement actif. Le texte d\'exécution est toujours GRATUIT — vous ne payez que quand vous générez réellement un média.',
                  '5 credits included every month with your active subscription. The execution text is always FREE — you only pay when you actually generate a media.'
                )}
              </p>
            </div>

            <button onClick={onClose}
              className="mt-5 w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform"
              data-testid="custom-ex-guide-got-it">
              {L("J'ai compris, c'est parti !", "Got it, let's go!")}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const CreditsBar = ({ en, credits, onBuy, buying }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const balance = credits ? credits.balance : null;
  return (
    <div className="flex items-center gap-2 shrink-0">
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/25 text-amber-300 text-sm font-bold" data-testid="custom-ex-credits-balance">
        <Coins className="w-4 h-4" /> {balance == null ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : balance}
      </div>
      <button
        onClick={onBuy} disabled={buying}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary/40 text-primary text-xs font-bold hover:bg-primary/10 disabled:opacity-50"
        data-testid="custom-ex-buy-credits-btn"
      >
        {buying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} {L('Crédits', 'Credits')}
      </button>
    </div>
  );
};

export const PackModal = ({ en, open, onClose, onPick, packs, busyPack }) => {
  const L = (fr, eng) => (en ? eng : fr);

  const predefinedPacks = [
    { credits: 5, price_chf: 5, original: null, discount: null },
    { credits: 25, price_chf: 20, original: 25, discount: '-20%' },
    { credits: 50, price_chf: 30, original: 50, discount: '-40%' },
    { credits: 100, price_chf: 50, original: 100, discount: '-50%', best: true },
  ];

  const getPackKey = (credits) => {
    if (!packs) return `pack_${credits}`;
    const found = Object.entries(packs).find(([k, p]) => p.credits === credits);
    return found ? found[0] : `pack_${credits}`;
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="custom-ex-pack-modal"
        >
          <div className="absolute inset-0" onClick={onClose} />
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 10 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="bg-zinc-950 border border-white/10 rounded-3xl p-5 sm:p-6 w-full max-w-md relative overflow-hidden shadow-2xl"
          >
            {/* Top background glow */}
            <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-[150%] h-40 bg-primary/20 blur-[60px] pointer-events-none rounded-[100%]" />

            <button onClick={onClose} className="absolute top-5 right-5 text-zinc-500 hover:text-white bg-white/5 hover:bg-white/10 rounded-full p-2 transition-colors z-10" data-testid="custom-ex-pack-modal-close">
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-6 relative z-10 pt-2">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-amber-500/20 border border-primary/30 mb-4 shadow-[0_0_20px_rgba(233,30,99,0.3)]">
                <Sparkles className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-2xl font-black text-white mb-2 tracking-tight">{L('Acheter des crédits', 'Buy credits')}</h3>
              <p className="text-sm text-zinc-400 font-medium px-4">{L("Générez plus de médias IA. Ces crédits n'expirent jamais.", "Generate more AI media. These credits never expire.")}</p>
            </div>

            <div className="space-y-3.5 relative z-10">
              {predefinedPacks.map((p) => {
                const packKey = getPackKey(p.credits);
                const isBest = p.best;

                return (
                  <motion.button
                    key={p.credits}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => onPick(packKey)}
                    disabled={!!busyPack}
                    className={`w-full relative group text-left transition-all duration-300 disabled:opacity-50 block
                      ${isBest
                        ? 'p-[2px] rounded-2xl bg-gradient-to-r from-amber-400 via-primary to-amber-400 animate-gradient-x shadow-[0_0_20px_rgba(233,30,99,0.2)]'
                        : 'p-4 rounded-2xl bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] hover:border-primary/50'
                      }
                    `}
                    data-testid={`custom-ex-pack-${p.credits}`}
                  >
                    {isBest && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-amber-400 to-primary text-[10px] font-black uppercase tracking-widest text-black shadow-lg z-20 whitespace-nowrap">
                        {L('🔥 Meilleure offre', '🔥 Best value')}
                      </span>
                    )}

                    <div className={`flex items-center justify-between gap-3 ${isBest ? 'bg-zinc-950 p-4 rounded-[14px] h-full' : ''}`}>
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0
                          ${isBest ? 'bg-gradient-to-br from-primary to-amber-500 text-white shadow-inner' : 'bg-white/5 text-amber-400 group-hover:text-primary transition-colors'}`}>
                          <Coins className="w-6 h-6" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-black text-xl leading-none">{p.credits}</span>
                            <span className="text-zinc-400 text-xs font-bold leading-none uppercase">{L('crédits', 'credits')}</span>
                          </div>
                          {p.discount && (
                            <div className="mt-1.5 inline-flex items-center gap-1.5">
                              <span className="text-[10px] font-bold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-1.5 py-0.5 rounded-md">
                                {p.discount}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="text-right flex flex-col items-end justify-center">
                        {p.original && (
                          <span className="text-xs text-zinc-500 font-semibold line-through mb-0.5 decoration-red-500/50 decoration-2">
                            {p.original} CHF
                          </span>
                        )}
                        <span className={`font-black text-2xl leading-none flex items-center gap-2
                          ${isBest ? 'text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-primary' : 'text-white'}
                        `}>
                          {busyPack === packKey ? <Loader2 className="w-6 h-6 animate-spin text-primary" /> : `${p.price_chf} CHF`}
                        </span>
                      </div>
                    </div>

                    {/* Shimmer effect inside the best card */}
                    {isBest && (
                      <div className="absolute inset-0 rounded-[14px] overflow-hidden pointer-events-none">
                        <div className="absolute inset-0 shimmer-effect opacity-30 mix-blend-overlay"></div>
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>

            <div className="mt-6 text-center text-[10px] text-zinc-500 flex items-center justify-center gap-2 font-medium">
               <Sparkles className="w-3.5 h-3.5" />
               {L('1 Image = 2 crédits | 1 Vidéo = 5 crédits', '1 Image = 2 credits | 1 Video = 5 credits')}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const NewExerciseForm = ({ en, open, onClose, onCreate, creating }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const [name, setName] = useState('');
  const [muscleGroup, setMuscleGroup] = useState('');
  const [equipment, setEquipment] = useState('');
  const [notes, setNotes] = useState('');
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="custom-ex-new-modal">
      <div className="bg-zinc-950 border border-white/10 rounded-2xl p-6 max-w-md w-full relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white" data-testid="custom-ex-new-modal-close"><X className="w-5 h-5" /></button>
        <h3 className="text-lg font-black text-white mb-4 flex items-center gap-2"><Wand2 className="w-5 h-5 text-primary" /> {L('Nouvel exercice', 'New exercise')}</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">{L('Nom *', 'Name *')}</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={L('Ex: Développé incliné haltères', 'Ex: Incline dumbbell press')}
              className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none" data-testid="custom-ex-name-input" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">{L('Groupe musculaire', 'Muscle group')}</label>
            <input value={muscleGroup} onChange={(e) => setMuscleGroup(e.target.value)} placeholder={L('Ex: Pectoraux', 'Ex: Chest')}
              className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none" data-testid="custom-ex-muscle-input" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">{L('Matériel', 'Equipment')}</label>
            <input value={equipment} onChange={(e) => setEquipment(e.target.value)} placeholder={L('Ex: Haltères, banc incliné', 'Ex: Dumbbells, incline bench')}
              className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none" data-testid="custom-ex-equipment-input" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">{L('Notes (déroulé du mouvement)', 'Notes (movement description)')}</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder={L('Décrivez le mouvement pour une génération plus fidèle...', 'Describe the movement for a more faithful generation...')}
              className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none resize-none" data-testid="custom-ex-notes-input" />
          </div>
          <button
            onClick={() => onCreate({ name, muscle_group: muscleGroup, equipment, notes })}
            disabled={creating || name.trim().length < 2}
            className="w-full mt-1 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="custom-ex-create-submit-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {L('Créer', 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Étape 1 obligatoire : texte d'exécution + conseils pro (IA, GRATUIT) — ancre le prompt
// image/vidéo pour un rendu fidèle, exactement comme côté admin. Sans lui, aucun média ne
// peut être généré (boutons Image/Storyboard/Vidéo désactivés en dessous).
const ExerciseTextSection = ({ en, ex, authFetch, onLocalUpdate }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const [expanded, setExpanded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null); // { execution, tips: [] }
  const [saving, setSaving] = useState(false);
  const hasText = !!(ex.execution && ex.execution.trim());

  const generateText = async () => {
    setGenerating(true);
    try {
      const res = await authFetch(`/api/tracking/custom-exercises/${ex.id}/generate-text`, { method: 'POST' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setDraft({ execution: data.execution || '', tips: data.tips && data.tips.length ? data.tips : ['', '', ''] });
      setExpanded(true);
    } catch {
      toast.error(L('Échec de la génération du texte', 'Text generation failed'));
    }
    setGenerating(false);
  };

  const saveText = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const tips = draft.tips.map((t) => t.trim()).filter(Boolean);
      const res = await authFetch(`/api/tracking/custom-exercises/${ex.id}`, {
        method: 'PATCH', body: JSON.stringify({ execution: draft.execution.trim(), tips }),
      });
      if (!res.ok) throw new Error();
      onLocalUpdate(ex.id, { execution: draft.execution.trim(), tips });
      setDraft(null);
      toast.success(L('Texte enregistré !', 'Text saved!'));
    } catch {
      toast.error(L('Échec de l\'enregistrement', 'Failed to save'));
    }
    setSaving(false);
  };

  if (!hasText && !draft) {
    return (
      <div className="rounded-xl bg-primary/10 border border-primary/30 p-3 space-y-2" data-testid={`custom-ex-text-cta-${ex.id}`}>
        <p className="text-xs font-bold text-primary flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> {L('Étape 1 — Texte d\'exécution', 'Step 1 — Execution text')}</p>
        <p className="text-[11px] text-zinc-400">{L('Obligatoire et gratuit — indispensable pour un média fidèle au mouvement.', 'Required and free — essential for a media faithful to the movement.')}</p>
        <button onClick={generateText} disabled={generating}
          className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-50"
          data-testid={`custom-ex-generate-text-btn-${ex.id}`}>
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {L('Générer le texte (gratuit)', 'Generate text (free)')}
        </button>
      </div>
    );
  }

  if (draft) {
    return (
      <div className="rounded-xl bg-white/[0.04] border border-primary/30 p-3 space-y-2" data-testid={`custom-ex-text-draft-${ex.id}`}>
        <p className="text-xs font-bold text-primary flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> {L('Relisez et ajustez si besoin', 'Review and adjust if needed')}</p>
        <textarea value={draft.execution} onChange={(e) => setDraft((d) => ({ ...d, execution: e.target.value }))}
          className="w-full bg-zinc-900 border border-white/10 rounded-lg p-2 text-xs text-white min-h-[70px]"
          data-testid={`custom-ex-text-draft-execution-${ex.id}`} />
        {draft.tips.map((t, i) => (
          <input key={i} value={t} onChange={(e) => setDraft((d) => ({ ...d, tips: d.tips.map((x, j) => j === i ? e.target.value : x) }))}
            placeholder={L(`Conseil ${i + 1}`, `Tip ${i + 1}`)}
            className="w-full bg-zinc-900 border border-white/10 rounded-lg p-2 text-[11px] text-white"
            data-testid={`custom-ex-text-draft-tip-${ex.id}-${i}`} />
        ))}
        <div className="flex items-center gap-2">
          <button onClick={saveText} disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-emerald-600 disabled:opacity-50"
            data-testid={`custom-ex-save-text-btn-${ex.id}`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {L('Enregistrer', 'Save')}
          </button>
          <button onClick={() => setDraft(null)} className="text-xs text-zinc-400 hover:text-white" data-testid={`custom-ex-cancel-text-btn-${ex.id}`}>
            {L('Annuler', 'Cancel')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-emerald-500/[0.06] border border-emerald-500/20 overflow-hidden" data-testid={`custom-ex-text-ready-${ex.id}`}>
      <button onClick={() => setExpanded((v) => !v)} className="w-full flex items-center justify-between p-2.5" data-testid={`custom-ex-text-toggle-${ex.id}`}>
        <span className="text-xs font-bold text-emerald-300 flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> {L('Texte d\'exécution prêt', 'Execution text ready')}</span>
        <ChevronDown className={`w-4 h-4 text-emerald-300 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          <p className="text-xs text-zinc-300 leading-relaxed">{ex.execution}</p>
          {(ex.tips || []).map((t, i) => (
            <p key={i} className="text-[11px] text-zinc-400 flex items-start gap-1.5"><span className="text-emerald-400">•</span>{t}</p>
          ))}
          <button onClick={generateText} disabled={generating}
            className="inline-flex items-center gap-1.5 text-[11px] font-bold text-primary hover:underline disabled:opacity-50"
            data-testid={`custom-ex-regenerate-text-btn-${ex.id}`}>
            {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} {L('Régénérer (gratuit)', 'Regenerate (free)')}
          </button>
        </div>
      )}
    </div>
  );
};

/** Upload d'une image de référence (JPG/PNG/WebP, 5 Mo max) — "telle quelle" (0 crédit,
 * aucune IA) ou comme référence pour guider la génération IA (coût standard image/storyboard,
 * vidéo Sora 2 exclue). Demande explicite du 29/09/2026 : pouvoir importer une photo trouvée
 * sur internet plutôt que de subir un rendu IA texte-seul parfois imprécis. */
const UploadRefModal = ({ en, ex, credits, authFetch, onLocalUpdate, onGenerateFromUpload, onClose }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const hasText = !!(ex.execution && ex.execution.trim());
  const [file, setFile] = useState(null);
  const [err, setErr] = useState('');
  const [uploading, setUploading] = useState(false);
  const [confirmMediaType, setConfirmMediaType] = useState(null);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr('');
    readUploadFile(f).then(setFile).catch(setErr);
  };

  const uploadAsIs = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await authFetch(`/api/tracking/custom-exercises/${ex.id}/upload-image`, {
        method: 'POST', body: JSON.stringify({ image_base64: file.base64, content_type: file.contentType }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      onLocalUpdate(ex.id, data.exercise);
      toast.success(L('Image importée !', 'Image imported!'));
      onClose();
    } catch { toast.error(L("Échec de l'import", 'Import failed')); }
    finally { setUploading(false); }
  };

  const confirmGenFromUpload = () => {
    if (!file || !confirmMediaType) return;
    onGenerateFromUpload(ex.id, confirmMediaType, file);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[130] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="upload-ref-modal">
      <div className="bg-zinc-950 border border-white/10 rounded-2xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-bold text-white flex items-center gap-2"><UploadCloud className="w-4 h-4 text-primary" /> {L('Importer une image', 'Import an image')}</h4>
          <button onClick={onClose} data-testid="upload-ref-close-btn"><X className="w-4 h-4 text-zinc-400 hover:text-white" /></button>
        </div>
        {!file ? (
          <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/15 rounded-xl py-8 cursor-pointer hover:border-primary/40 transition-colors" data-testid="upload-ref-dropzone">
            <UploadCloud className="w-7 h-7 text-zinc-500" />
            <span className="text-xs text-zinc-500">{L('JPG, PNG ou WebP — 5 Mo max', 'JPG, PNG or WebP — 5 MB max')}</span>
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFileChange} data-testid="upload-ref-file-input" />
          </label>
        ) : (
          <div className="space-y-3">
            <img src={file.previewUrl} alt="preview" className="w-full aspect-square object-cover rounded-xl border border-white/10" data-testid="upload-ref-preview" />
            <button onClick={uploadAsIs} disabled={uploading}
              className="w-full py-2.5 rounded-xl font-bold text-sm text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="upload-ref-as-is-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {L('Garder telle quelle (gratuit)', 'Keep as-is (free)')}
            </button>
            <p className="text-[11px] text-zinc-500 text-center">{L("ou générer avec l'IA en utilisant cette image comme référence :", 'or generate with AI using this image as reference:')}</p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setConfirmMediaType('image')} disabled={!hasText || (credits && credits.balance < MEDIA_LABELS.image.cost)}
                className="py-2 rounded-lg text-xs font-bold text-zinc-200 border border-white/10 hover:border-primary/40 disabled:opacity-30 transition-colors"
                data-testid="upload-ref-gen-image-btn">
                {L(`Image (-${MEDIA_LABELS.image.cost})`, `Image (-${MEDIA_LABELS.image.cost})`)}
              </button>
              <button onClick={() => setConfirmMediaType('storyboard')} disabled={!hasText || (credits && credits.balance < MEDIA_LABELS.storyboard.cost)}
                className="py-2 rounded-lg text-xs font-bold text-zinc-200 border border-white/10 hover:border-primary/40 disabled:opacity-30 transition-colors"
                data-testid="upload-ref-gen-storyboard-btn">
                {L(`Storyboard (-${MEDIA_LABELS.storyboard.cost})`, `Storyboard (-${MEDIA_LABELS.storyboard.cost})`)}
              </button>
            </div>
            {!hasText && <p className="text-[10px] text-amber-400 text-center">{L("Générez d'abord le texte d'exécution pour débloquer l'IA.", 'Generate the execution text first to unlock AI.')}</p>}
            <button onClick={() => setFile(null)} className="w-full text-[11px] text-zinc-500 hover:text-white text-center" data-testid="upload-ref-change-file-btn">
              {L('Changer de fichier', 'Change file')}
            </button>
          </div>
        )}
        {err && <p className="text-xs text-red-400 mt-2" data-testid="upload-ref-error">{err}</p>}
      </div>
      {confirmMediaType && (
        <div className="fixed inset-0 z-[140] bg-black/85 flex items-center justify-center p-4" onClick={() => setConfirmMediaType(null)}>
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5 max-w-xs w-full" onClick={(e) => e.stopPropagation()} data-testid="upload-ref-confirm-modal">
            <h4 className="font-bold text-white mb-2">{L('Confirmer la génération ?', 'Confirm generation?')}</h4>
            <p className="text-zinc-400 text-sm mb-4">
              {L(`Cela utilisera ${MEDIA_LABELS[confirmMediaType].cost} crédit(s) pour générer ${confirmMediaType === 'image' ? 'une image' : 'un storyboard'} de "${ex.name}" à partir de votre photo.`,
                 `This will use ${MEDIA_LABELS[confirmMediaType].cost} credit(s) to generate a ${MEDIA_LABELS[confirmMediaType].en.toLowerCase()} of "${ex.name}" from your photo.`)}
            </p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmMediaType(null)} className="flex-1 px-3 py-2 rounded-lg text-sm font-semibold text-zinc-400 bg-white/5 hover:bg-white/10" data-testid="upload-ref-confirm-cancel-btn">{L('Annuler', 'Cancel')}</button>
              <button onClick={confirmGenFromUpload} className="flex-1 px-3 py-2 rounded-lg text-sm font-bold text-white bg-primary hover:bg-primary/90" data-testid="upload-ref-confirm-btn">
                {L(`Confirmer (-${MEDIA_LABELS[confirmMediaType].cost})`, `Confirm (-${MEDIA_LABELS[confirmMediaType].cost})`)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ExerciseCard = ({ en, ex, credits, onGenerate, onDelete, onShare, genBusyType, authFetch, onLocalUpdate, onGenerateFromUpload }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const generating = ex.media_status === 'generating';
  const failed = ex.media_status === 'failed';
  const ready = ex.media_status === 'ready' && ex.media_url;
  const hasText = !!(ex.execution && ex.execution.trim());
  const [confirmType, setConfirmType] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  return (
    <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 overflow-hidden" data-testid={`custom-ex-card-${ex.id}`}>
      <div className="aspect-video bg-zinc-900 flex items-center justify-center relative">
        {ready ? (
          ex.media_type === 'video' ? (
            <video src={`${backendUrl}${ex.media_url}`} className="w-full h-full object-cover" autoPlay loop muted playsInline data-testid={`custom-ex-media-${ex.id}`} />
          ) : (
            <img src={`${backendUrl}${ex.media_url}`} alt={ex.name} className="w-full h-full object-cover" data-testid={`custom-ex-media-${ex.id}`} />
          )
        ) : generating ? (
          <div className="flex flex-col items-center gap-2 text-zinc-500 text-xs px-3 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            {L('Génération en cours... (jusqu\'à 5 min pour une vidéo)', 'Generating... (up to 5 min for a video)')}
          </div>
        ) : failed ? (
          <div className="flex flex-col items-center gap-2 text-red-400 text-xs px-3 text-center">
            <AlertTriangle className="w-6 h-6" /> {L('Échec — crédit remboursé', 'Failed — credit refunded')}
          </div>
        ) : (
          <ImageIcon className="w-8 h-8 text-zinc-700" />
        )}
        <span
          className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/90 text-black text-[10px] font-black uppercase tracking-wide shadow-sm"
          data-testid={`custom-ex-badge-mine-${ex.id}`}
        >
          <Sparkles className="w-2.5 h-2.5" /> {L('Créé par vous', 'Made by you')}
        </span>
        <button
          onClick={() => onShare(ex.id)}
          className="absolute top-2 right-11 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-zinc-400 hover:text-primary"
          data-testid={`custom-ex-share-${ex.id}`}
          title={L('Partager (lien public, sans connexion)', 'Share (public link, no login)')}
        >
          <Share2 className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => setUploadModalOpen(true)}
          className="absolute top-2 right-20 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-zinc-400 hover:text-primary"
          data-testid={`custom-ex-upload-${ex.id}`}
          title={L('Importer une image (telle quelle ou référence IA)', 'Import an image (as-is or AI reference)')}
        >
          <UploadCloud className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onDelete(ex.id)}
          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-zinc-400 hover:text-red-400"
          data-testid={`custom-ex-delete-${ex.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="p-3.5 space-y-2.5">
        <div>
          <p className="text-white font-bold text-sm truncate">{ex.name}</p>
          <p className="text-xs text-zinc-500 truncate">{[ex.muscle_group, ex.equipment].filter(Boolean).join(' · ') || L('Sans détail', 'No detail')}</p>
        </div>

        <ExerciseTextSection en={en} ex={ex} authFetch={authFetch} onLocalUpdate={onLocalUpdate} />

        <div className="flex gap-1.5">
          {Object.entries(MEDIA_LABELS).map(([type, info]) => {
            const Icon = info.icon;
            const busy = genBusyType === `${ex.id}:${type}`;
            const disabled = !hasText || generating || busy || (credits && credits.balance < info.cost);
            return (
              <button
                key={type} onClick={() => setConfirmType(type)} disabled={disabled}
                title={!hasText ? L('Générez d\'abord le texte d\'exécution', 'Generate the execution text first') : `${en ? info.en : info.fr} (-${info.cost})`}
                className="flex-1 inline-flex flex-col items-center gap-0.5 py-1.5 rounded-lg border border-white/10 text-[10px] font-bold text-zinc-300 hover:border-primary/40 disabled:opacity-30 transition-colors"
                data-testid={`custom-ex-gen-${type}-${ex.id}`}
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (ready && ex.media_type === type ? <RefreshCw className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />)}
                <span>{en ? info.en : info.fr} -{info.cost}</span>
              </button>
            );
          })}
        </div>
      </div>
      {confirmType && (
        <div className="fixed inset-0 z-[150] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setConfirmType(null)}>
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5 max-w-xs w-full" onClick={(e) => e.stopPropagation()} data-testid="generate-confirm-modal">
            <h4 className="font-bold text-white mb-2">{L('Confirmer la génération ?', 'Confirm generation?')}</h4>
            <p className="text-zinc-400 text-sm mb-4">
              {L(`Cela utilisera ${MEDIA_LABELS[confirmType].cost} crédit(s) pour générer ${confirmType === 'image' ? 'une image' : confirmType === 'storyboard' ? 'un storyboard' : 'une vidéo'} de "${ex.name}".`,
                 `This will use ${MEDIA_LABELS[confirmType].cost} credit(s) to generate a ${MEDIA_LABELS[confirmType].en.toLowerCase()} of "${ex.name}".`)}
            </p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmType(null)} className="flex-1 px-3 py-2 rounded-lg text-sm font-semibold text-zinc-400 bg-white/5 hover:bg-white/10" data-testid="generate-confirm-cancel-btn">{L('Annuler', 'Cancel')}</button>
              <button
                onClick={() => { onGenerate(ex.id, confirmType); setConfirmType(null); }}
                className="flex-1 px-3 py-2 rounded-lg text-sm font-bold text-white bg-primary hover:bg-primary/90"
                data-testid="generate-confirm-btn"
              >
                {L(`Confirmer (-${MEDIA_LABELS[confirmType].cost})`, `Confirm (-${MEDIA_LABELS[confirmType].cost})`)}
              </button>
            </div>
          </div>
        </div>
      )}
      {uploadModalOpen && (
        <UploadRefModal en={en} ex={ex} credits={credits} authFetch={authFetch} onLocalUpdate={onLocalUpdate}
          onGenerateFromUpload={onGenerateFromUpload} onClose={() => setUploadModalOpen(false)} />
      )}
    </div>
  );
};

export const CustomExerciseLibrary = ({ en, onBack }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [exercises, setExercises] = useState([]);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [packModalOpen, setPackModalOpen] = useState(false);
  const [busyPack, setBusyPack] = useState(null);
  const [genBusyType, setGenBusyType] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const pollRefs = useRef({});

  const loadAll = useCallback(async () => {
    try {
      const [exRes, crRes] = await Promise.all([
        authFetch('/api/tracking/custom-exercises'),
        authFetch('/api/tracking/custom-exercises/credits/state'),
      ]);
      setExercises((await exRes.json()).exercises || []);
      setCredits(await crRes.json());
    } catch { /* noop */ }
    setLoading(false);
  }, [authFetch]);
  useEffect(() => { loadAll(); }, [loadAll]);

  // Retour de Stripe (achat de crédits) → confirmer le paiement puis créditer.
  useEffect(() => {
    const sid = searchParams.get('custom_ex_credit_session');
    if (!sid) return;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await authFetch(`/api/tracking/custom-exercises/credits/checkout-status/${sid}`);
        const d = await r.json();
        if (d.payment_status === 'paid') {
          searchParams.delete('custom_ex_credit_session');
          setSearchParams(searchParams, { replace: true });
          setCredits(d);
          toast.success(L('Crédits ajoutés !', 'Credits added!'));
          return;
        }
      } catch { /* retry */ }
      if (attempts < 6) setTimeout(tick, 2000);
    };
    tick();
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { Object.values(pollRefs.current).forEach(clearTimeout); }, []);

  const onLocalUpdate = useCallback((id, patch) => {
    setExercises((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  }, []);

  const pollExercise = useCallback((id, mediaType) => {
    // Une vidéo Sora 2 peut prendre jusqu'à 5 min — attente bien plus longue qu'une image.
    const maxAttempts = mediaType === 'video' ? 100 : 30;
    const intervalMs = mediaType === 'video' ? 6000 : 3000;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await authFetch(`/api/tracking/custom-exercises/${id}`);
        const d = await r.json();
        setExercises((prev) => prev.map((e) => (e.id === id ? d.exercise : e)));
        if (d.exercise.media_status === 'generating' && attempts < maxAttempts) {
          pollRefs.current[id] = setTimeout(tick, intervalMs);
        }
      } catch { /* noop */ }
    };
    pollRefs.current[id] = setTimeout(tick, intervalMs);
  }, [authFetch]);

  const handleCreate = async (payload) => {
    if (!payload.name || payload.name.trim().length < 2) return;
    setCreating(true);
    try {
      const r = await authFetch('/api/tracking/custom-exercises', { method: 'POST', body: JSON.stringify(payload) });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setExercises((prev) => [d.exercise, ...prev]);
      setNewModalOpen(false);
      if (d.exercise.welcome_bonus_granted) {
        toast.success(L('🎁 Exercice créé ! +2 crédits offerts pour découvrir la génération IA.', '🎁 Exercise created! +2 free credits to try AI generation.'));
        setCredits((prev) => (prev ? { ...prev, purchased_remaining: prev.purchased_remaining + 2, balance: prev.balance + 2 } : prev));
      } else {
        toast.success(L('Exercice créé ! Générez maintenant son texte d\'exécution (gratuit).', 'Exercise created! Now generate its execution text (free).'));
      }
    } catch {
      toast.error(L('Erreur lors de la création.', 'Error creating exercise.'));
    }
    setCreating(false);
  };

  const handleGenerate = async (id, type) => {
    setGenBusyType(`${id}:${type}`);
    try {
      const r = await authFetch(`/api/tracking/custom-exercises/${id}/generate`, { method: 'POST', body: JSON.stringify({ media_type: type }) });
      if (r.status === 402) { toast.error(L('Crédits insuffisants.', 'Not enough credits.')); setPackModalOpen(true); setGenBusyType(null); return; }
      if (r.status === 400) { toast.error(L('Générez d\'abord le texte d\'exécution.', 'Generate the execution text first.')); setGenBusyType(null); return; }
      if (!r.ok) throw new Error();
      setExercises((prev) => prev.map((e) => (e.id === id ? { ...e, media_status: 'generating' } : e)));
      const crRes = await authFetch('/api/tracking/custom-exercises/credits/state');
      setCredits(await crRes.json());
      pollExercise(id, type);
    } catch {
      toast.error(L('Erreur lors du lancement de la génération.', 'Error starting generation.'));
    }
    setGenBusyType(null);
  };

  const handleGenerateFromUpload = async (id, type, file) => {
    setGenBusyType(`${id}:${type}`);
    try {
      const r = await authFetch(`/api/tracking/custom-exercises/${id}/generate-from-upload`, {
        method: 'POST', body: JSON.stringify({ image_base64: file.base64, content_type: file.contentType, media_type: type }),
      });
      if (r.status === 402) { toast.error(L('Crédits insuffisants.', 'Not enough credits.')); setPackModalOpen(true); setGenBusyType(null); return; }
      if (r.status === 400) { toast.error(L('Générez d\'abord le texte d\'exécution.', 'Generate the execution text first.')); setGenBusyType(null); return; }
      if (r.status === 422) { toast.error(L('Image invalide.', 'Invalid image.')); setGenBusyType(null); return; }
      if (!r.ok) throw new Error();
      setExercises((prev) => prev.map((e) => (e.id === id ? { ...e, media_status: 'generating' } : e)));
      const crRes = await authFetch('/api/tracking/custom-exercises/credits/state');
      setCredits(await crRes.json());
      pollExercise(id, type);
    } catch {
      toast.error(L('Erreur lors du lancement de la génération.', 'Error starting generation.'));
    }
    setGenBusyType(null);
  };

  const handleDelete = async (id) => {
    try {
      await authFetch(`/api/tracking/custom-exercises/${id}`, { method: 'DELETE' });
      setExercises((prev) => prev.filter((e) => e.id !== id));
    } catch { toast.error(L('Erreur.', 'Error.')); }
  };

  const handleShare = async (id) => {
    try {
      const r = await authFetch(`/api/tracking/custom-exercises/${id}/share`, { method: 'POST' });
      const d = await r.json();
      const url = `${window.location.origin}/exercice-partage/${d.share_token}`;
      if (navigator.share) {
        await navigator.share({ title: 'Body-Up — Exercice créé', url }).catch(() => {});
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(L('Lien copié dans le presse-papiers !', 'Link copied to clipboard!'));
      }
    } catch { toast.error(L('Impossible de partager cet exercice', 'Could not share this exercise')); }
  };

  const handlePickPack = async (pack) => {
    setBusyPack(pack);
    try {
      const r = await authFetch('/api/tracking/custom-exercises/credits/checkout', {
        method: 'POST', body: JSON.stringify({ pack, origin_url: window.location.origin }),
      });
      const d = await r.json();
      if (d.url) window.location.href = d.url;
    } catch { toast.error(L('Erreur de paiement.', 'Payment error.')); setBusyPack(null); }
  };

  return (
    <div className="space-y-4" data-testid="custom-exercise-library">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="custom-ex-back-btn">
          <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => setGuideOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-300 border border-white/10 hover:border-primary/50 hover:text-white transition-colors"
            data-testid="custom-ex-open-guide-btn">
            <HelpCircle className="w-3.5 h-3.5" /> {L('Guide & conseils', 'Guide & tips')}
          </button>
          <CreditsBar en={en} credits={credits} onBuy={() => setPackModalOpen(true)} buying={!!busyPack} />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[11px] text-zinc-500">{L('Reçois une alerte quand ta vidéo Sora 2 est prête, sans surveiller l\'écran.', 'Get alerted when your Sora 2 video is ready, no need to watch the screen.')}</p>
        <PushOptIn en={en} />
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-xl font-black text-white flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" /> {L('Ma bibliothèque créée', 'My created library')}
        </h2>
        <button
          onClick={() => setNewModalOpen(true)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-105 transition-transform"
          data-testid="custom-ex-new-btn"
        >
          <Plus className="w-4 h-4" /> {L('Nouvel exercice', 'New exercise')}
        </button>
      </div>
      <p className="text-xs text-zinc-500">
        {L('Créez vos propres exercices : générez d\'abord leur texte d\'exécution (gratuit), puis une image (-2), un storyboard (-1) ou une vraie vidéo Sora 2 (-5).', 'Create your own exercises: first generate their execution text (free), then an image (-2), a storyboard (-1) or a real Sora 2 video (-5).')}
      </p>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : exercises.length === 0 ? (
        <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 p-8 text-center text-zinc-500 text-sm" data-testid="custom-ex-empty">
          {L('Aucun exercice créé pour le moment. Cliquez sur "Nouvel exercice" pour commencer !', 'No exercise created yet. Click "New exercise" to start!')}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5" data-testid="custom-ex-grid">
          {exercises.map((ex) => (
            <ExerciseCard key={ex.id} en={en} ex={ex} credits={credits} onGenerate={handleGenerate} onDelete={handleDelete} onShare={handleShare}
              genBusyType={genBusyType} authFetch={authFetch} onLocalUpdate={onLocalUpdate} onGenerateFromUpload={handleGenerateFromUpload} />
          ))}
        </div>
      )}

      <NewExerciseForm en={en} open={newModalOpen} onClose={() => setNewModalOpen(false)} onCreate={handleCreate} creating={creating} />
      <PackModal en={en} open={packModalOpen} onClose={() => setPackModalOpen(false)} onPick={handlePickPack} packs={credits?.packs} busyPack={busyPack} />
      <GuideModal en={en} open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
};

export default CustomExerciseLibrary;
